import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { formatMomentDate } from "../../../utils/dateHelper";
import CustomSelect from "../../form/CustomSelect";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfileConfig } from "../../../redux/slice/allUsersSlice";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";

const ExperienceModal = ({ onClose, onSubmit, experience, isLoading }) => {
  const dispatch = useDispatch();
  const { profileConfig } = useSelector((state) => state.allUsers);
  const toast = useToast();
  const [formData, setFormData] = useState({
    job_title: "",
    organization: "",
    employment_type: "",
    location: "",
    location_type: "",
    from_date: "",
    to_date: "",
    is_current: false,
    // description: "",
  });

  const transformedEmploymentTypes = (profileConfig?.employment_types || []).map((type) => ({
    value: type,
    label: type,
  }));

  const transformedLocationTypes = (profileConfig?.location_types || []).map((type) => ({
    value: type,
    label: type,
  }));

  useEffect(() => {
    if (!profileConfig) {
      dispatch(fetchProfileConfig());
    }
    if (experience) {
      setFormData({
        job_title: experience.job_title || "",
        organization: experience.organization || "",
        employment_type: experience.employment_type || "",
        location: experience.location || "",
        location_type: experience.location_type || "",
        from_date: experience.from_date ? formatMomentDate(experience.from_date, "YYYY-MM-DD") : "",
        to_date: experience.to_date ? formatMomentDate(experience.to_date, "YYYY-MM-DD") : "",
        is_current: experience.is_current || false,
        // description: experience.description || "",
      });
    } else {
      setFormData({
        job_title: "",
        organization: "",
        employment_type: "",
        location: "",
        location_type: "",
        from_date: "",
        to_date: "",
        is_current: false,
        // description: "",
      });
    }
  }, [experience, profileConfig, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      // Clear end date immediately when "currently working" is checked
      if (name === "is_current" && checked) {
        updated.to_date = "";
      }
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    // Detailed Validation Checks
    if (!formData.job_title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.employment_type) {
      toast.error("Employment type is required");
      return;
    }
    if (!formData.organization.trim()) {
      toast.error("Company is required");
      return;
    }
    if (!formData.location_type) {
      toast.error("Location type is required");
      return;
    }
    if (!formData.from_date) {
      toast.error("Start date is required");
      return;
    }

    const todayStr = new Date().toLocaleDateString('en-CA');
    if (formData.from_date && formData.from_date > todayStr) {
      toast.error("Start date cannot be in the future");
      return;
    }

    if (!formData.is_current) {
      if (!formData.to_date) {
        toast.error("Please provide end date or mark as current");
        return;
      }
      if (formData.from_date >= formData.to_date) {
        toast.error("End date must be after start date");
        return;
      }
    }

    const payload = { ...formData };
    if (payload.is_current) {
      payload.to_date = null;
    }

    onSubmit(payload);
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Modal.Body className="grid gap-3">


        <InputField
          className="g-col-md-6"
          label={{ name: "Title" }}
          required
          input={{
            type: "text",
            name: "job_title",
            value: formData.job_title,
            onChange: handleChange,
            placeholder: "Ex: Software Engineer",
          }}
        />

        <CustomSelect
          label={{ name: "Employment Type" }}
          name="employment_type"
          value={formData.employment_type}
          options={transformedEmploymentTypes}
          onChange={(selected) =>
            setFormData((prev) => ({
              ...prev,
              employment_type: selected ? selected.value : "",
            }))
          }
          placeholder="Select Employment Type"
          required={true}
          className="g-col-md-6"
        />
        <InputField
          className="g-col-md-12"
          label={{ name: "Company" }}
          required
          input={{
            type: "text",
            name: "organization",
            value: formData.organization,
            onChange: handleChange,
            placeholder: "Ex: Microsoft",
          }}
        />

        <InputField
          label={{ name: "Location" }}
          className="g-col-md-6"
          input={{
            type: "text",
            name: "location",
            value: formData.location,
            onChange: handleChange,
            placeholder: "Ex: London, United Kingdom",
          }}
        />

        <CustomSelect
          label={{ name: "Location Type" }}
          name="location_type"
          value={formData.location_type}
          options={transformedLocationTypes}
          onChange={(selected) =>
            setFormData((prev) => ({
              ...prev,
              location_type: selected ? selected.value : "",
            }))
          }
          placeholder="Select Location Type"
          required={true}
          className="g-col-md-6"
        />
        <div className="g-col-md-12">
          <Form.Check
            type="checkbox"
            label="I am currently working in this role"
            name="is_current"
            checked={formData.is_current}
            onChange={handleChange}
            id="is_current_checkbox"
          />
        </div>
        <InputField
          className="g-col-md-6"
          label={{ name: "Start Date" }}
          required
          input={{
            type: "date",
            name: "from_date",
            value: formData.from_date,
            onChange: handleChange,
            max: new Date().toLocaleDateString('en-CA'),
          }}
        />
        <InputField
          className="g-col-md-6"
          label={{ name: "End Date" }}
          required={!formData.is_current}
          input={{
            type: "date",
            name: "to_date",
            value: formData.to_date,
            onChange: handleChange,
            disabled: !formData.from_date || formData.is_current,
            min: formData.from_date,
            max: new Date().toLocaleDateString('en-CA'),
          }}
        />
        {/* <div className="g-col-md-12">
            <InputField
              label={{ name: "Description" }}
              input={{
                as: "textarea",
                rows: 3,
                name: "description",
                value: formData.description,
                onChange: handleChange,
                placeholder: "Describe your responsibilities and accomplishments",
              }}
            />
          </div> */}

      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title={experience ? "Update" : "Save"}
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};

export default withModal(ExperienceModal);
